import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useTranslation } from 'react-i18next';

/**
 * 이메일 인증 유예 기간 관리 훅
 * @param {Object} user - Firebase 사용자 객체
 * @returns {object} - 유예 기간 관련 상태와 함수들
 */
export const useEmailVerificationGrace = (user) => {
  const { t } = useTranslation();
  const [graceInfo, setGraceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // 테스트용: 30초 = 30 * 1000 밀리초 (원래: 24시간 = 24 * 60 * 60 * 1000)
  const GRACE_PERIOD_MS = 24 * 60 * 60 * 1000;

  /**
   * 사용자의 유예 기간 정보를 가져오는 함수
   */
  const fetchGraceInfo = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true); // 새로고침 시 로딩 상태 설정
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // 이메일 로그인 사용자만 유예 기간 적용
        if (userData.loginMethod === 'email') {
          const createdAt = userData.createdAt?.toDate();
          const graceEndTime = new Date(createdAt.getTime() + GRACE_PERIOD_MS);
          
          setGraceInfo({
            createdAt,
            graceEndTime,
            loginMethod: userData.loginMethod,
            emailVerified: user.emailVerified
          });
          
          console.log('📧 [EmailVerificationGrace] 이메일 사용자 유예 기간 정보 업데이트:', {
            isEmailVerified: user.emailVerified,
            createdAt,
            graceEndTime
          });
        } else {
          // 이메일 로그인이 아닌 사용자는 유예 기간 없음
          setGraceInfo(null);
        }
      } else {
        console.warn('⚠️ [EmailVerificationGrace] 사용자 문서를 찾을 수 없음:', user.uid);
        setGraceInfo(null);
      }
    } catch (error) {
      console.error('❌ [EmailVerificationGrace] 유예 기간 정보 조회 실패:', error);
      setGraceInfo(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 남은 시간을 계산하는 함수
   */
  const calculateTimeRemaining = () => {
    if (!graceInfo || user?.emailVerified) {
      setTimeRemaining(null);
      return;
    }

    const now = new Date();
    const remaining = graceInfo.graceEndTime.getTime() - now.getTime();
    
    if (remaining <= 0) {
      setTimeRemaining(0);
    } else {
      setTimeRemaining(remaining);
    }
  };

  /**
   * 유예 기간이 만료되었는지 확인
   */
  const isGraceExpired = () => {
    if (!graceInfo || user?.emailVerified) return false;
    return new Date() > graceInfo.graceEndTime;
  };

  /**
   * 유예 기간 중인지 확인
   */
  const isInGracePeriod = () => {
    if (!graceInfo || user?.emailVerified) return false;
    return graceInfo.loginMethod === 'email' && !user.emailVerified && !isGraceExpired();
  };

  /**
   * 시간을 포맷팅하는 함수
   */
  const formatTimeRemaining = () => {
    if (!timeRemaining || timeRemaining <= 0) return null;
    
    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return t('time.hoursMinutes', { hours, minutes });
    } else {
      return t('time.minutes', { minutes });
    }
  };

  // 초기 데이터 로드 (user.uid 추가로 회원가입 시 즉시 감지)
  useEffect(() => {
    fetchGraceInfo();
  }, [user, user?.uid]);

  // 1분마다 남은 시간 업데이트 및 이메일 인증 상태 변경 감지
  useEffect(() => {
    if (!graceInfo) return;

    calculateTimeRemaining();
    
    const interval = setInterval(() => {
      calculateTimeRemaining();
    }, 60000); // 1분마다 업데이트

    return () => clearInterval(interval);
  }, [graceInfo, user?.emailVerified]);

  // 이메일 인증 상태가 변경되면 정보 새로고침
  useEffect(() => {
    if (user?.emailVerified && graceInfo) {
      console.log('📧 [EmailVerificationGrace] 이메일 인증 완료 감지 - 상태 새로고침');
      fetchGraceInfo();
    }
  }, [user?.emailVerified]);

  // Firebase Auth 상태 변경 감지 (토큰 갱신 등)
  useEffect(() => {
    if (user) {
      // 사용자가 있을 때 주기적으로 토큰 상태 체크
      const checkAuthState = async () => {
        try {
          await user.reload(); // Firebase 사용자 정보 새로고침
          if (user.emailVerified !== graceInfo?.emailVerified) {
            console.log('📧 [EmailVerificationGrace] 인증 상태 변경 감지 - 새로고침 시작');
            fetchGraceInfo();
          }
        } catch (error) {
          console.error('❌ [EmailVerificationGrace] Auth 상태 체크 실패:', error);
        }
      };

      // 초기 체크
      checkAuthState();
      
      // 10초마다 체크 (인증 완료 감지를 위해)
      const interval = setInterval(checkAuthState, 10000);
      
      return () => clearInterval(interval);
    }
  }, [user, graceInfo?.emailVerified]);

  return {
    graceInfo,
    loading,
    timeRemaining,
    isGraceExpired: isGraceExpired(),
    isInGracePeriod: isInGracePeriod(),
    formatTimeRemaining: formatTimeRemaining(),
    refreshGraceInfo: fetchGraceInfo
  };
}; 